import { calendarService } from '@/services/calendar.service';
import {
  SDKCalendarEventDto,
  CalendarStatsResponse
} from '@/types/calendar.types';
import { SkeletonLoader, CardSkeleton } from '@/components/ui/SkeletonLoader';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  XCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTracking, useTimeTracking, useScrollTracking } from '@/hooks/useAnalytics';
import { trackEvent } from '@/utils/analytics';
import { LEARNER_SCHEDULE_EVENTS } from '@/constants/analytics.events';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_SCHEDULE_PERMISSION, LEARNER_COURSES_PERMISSION } from '@/constants/constant-permissions';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: SDKCalendarEventDto[];
}

export default function LearnerSchedule() {
  // Permission guard
  usePermissionRouteAccess([LEARNER_SCHEDULE_PERMISSION.LEARNER_SCHEDULE_READ]);
  const canWrite = useHasPermission([LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE]);

  const navigate = useNavigate();

  // Analytics hooks
  usePageTracking('/learn/schedule');
  useTimeTracking('/learn/schedule');
  useScrollTracking('/learn/schedule');

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SDKCalendarEventDto | null>(null);

  // SDK Response data - categorized by status (from stats endpoint)
  const [allEvents, setAllEvents] = useState<SDKCalendarEventDto[]>([]);
  const [overdueEvents, setOverdueEvents] = useState<SDKCalendarEventDto[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<SDKCalendarEventDto[]>([]);
  const [todayEvents, setTodayEvents] = useState<SDKCalendarEventDto[]>([]);
  const [calendarStats, setCalendarStats] = useState<CalendarStatsResponse | null>(null);


  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get event date from SDK event (uses deadline_date)
   */
  const getEventDate = (event: SDKCalendarEventDto): Date => {
    return new Date(event.deadline_date);
  };

  /**
   * Get event title from SDK event
   */
  const getEventTitle = (event: SDKCalendarEventDto): string => {
    return event.title || event.course_title;
  };

  /**
   * Calculate which week of the month a date falls in
   */
  const getWeekOfMonth = (date: Date): number => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayWeekday = firstDayOfMonth.getDay();
    const offsetDate = date.getDate() + firstDayWeekday - 1;
    return Math.ceil(offsetDate / 7);
  };

  /**
   * Map period name to viewMode for consistency
   */
  const getPeriodFromViewMode = (mode: string): 'day' | 'week' | 'month' => {
    return (mode === 'agenda' ? 'month' : mode) as any;
  };

  /**
   * Calculate date range based on viewMode and selected date
   */
  const getDateRangeForPeriod = (period: 'day' | 'week' | 'month', date: Date = selectedDate): { startDate: string; endDate: string } => {
    const startDate = new Date(date);
    const endDate = new Date(date);

    switch (period) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'week':
        startDate.setDate(date.getDate() - date.getDay());
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'month':
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const calendarStart = new Date(firstDay);
        calendarStart.setDate(firstDay.getDate() - firstDay.getDay());
        calendarStart.setHours(0, 0, 0, 0);

        const calendarEnd = new Date(calendarStart);
        calendarEnd.setDate(calendarStart.getDate() + 41);
        calendarEnd.setHours(23, 59, 59, 999);

        startDate.setTime(calendarStart.getTime());
        endDate.setTime(calendarEnd.getTime());
        break;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  /**
   * Fetch calendar events (for month view - gets previous_week and upcoming_week)
   */
  const fetchCalendarEvents = async (currentViewMode: string = viewMode) => {
    try {
      setLoading(true);
      setError(null);

      const period = getPeriodFromViewMode(currentViewMode);

      if (period !== 'month') {
        // Date range computed for future API integration
        void getDateRangeForPeriod(period, currentViewMode === viewMode ? selectedDate : new Date());
      }

    } catch (err: any) {
      console.error('Error fetching calendar events:', err);
      setError(err.message || 'Failed to load calendar events');

    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch global calendar stats (includes overdue_courses, upcoming_deadlines, today_schedule)
   */
  const fetchCalendarStats = async () => {
    try {
      setStatsLoading(true);
      const statsResponse = await calendarService.getCalendarStatsByPeriod() as CalendarStatsResponse;

      if (statsResponse) {
        setCalendarStats(statsResponse);

        // Set the event lists from stats
        setOverdueEvents(statsResponse.overdue_courses || []);
        setUpcomingEvents(statsResponse.upcoming_deadlines || []);
        setTodayEvents(statsResponse.today_schedule || []);

        // Combine all events for calendar display
        const combined = [
          ...(statsResponse.overdue_courses || []),
          ...(statsResponse.upcoming_deadlines || []),
          ...(statsResponse.today_schedule || [])
        ];
        setAllEvents(combined);
      }
    } catch (err: any) {
      console.error('Error fetching calendar stats:', err);
      setCalendarStats(null);
      setAllEvents([]);
      setOverdueEvents([]);
      setUpcomingEvents([]);
      setTodayEvents([]);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarEvents(viewMode);
  }, [viewMode, selectedDate, currentMonth]);

  useEffect(() => {
    fetchCalendarStats();
  }, []);

  // Track widgets on mount
  useEffect(() => {
    trackEvent(LEARNER_SCHEDULE_EVENTS.WIDGET_STATS_VIEW);
    trackEvent(LEARNER_SCHEDULE_EVENTS.WIDGET_CALENDAR_VIEW);
    trackEvent(LEARNER_SCHEDULE_EVENTS.WIDGET_TODAY_SCHEDULE_VIEW);
    trackEvent(LEARNER_SCHEDULE_EVENTS.WIDGET_UPCOMING_DEADLINES_VIEW);
    trackEvent(LEARNER_SCHEDULE_EVENTS.WIDGET_OVERDUE_VIEW);
  }, []);

  /**
   * Generate calendar days for month view
   * Shows from first day of month to last day of month (including partial weeks)
   */
  const generateCalendarDays = (month: Date): CalendarDay[] => {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0); // Last day of current month

    // Start from the Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    // End on the Saturday of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate the number of days to show
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const dayEvents = allEvents.filter(event => {
        const eventDate = getEventDate(event);
        return eventDate.toDateString() === date.toDateString();
      });

      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === month.getMonth(),
        isToday: date.getTime() === today.getTime(),
        events: dayEvents
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays(currentMonth);

  const selectedDateEvents = allEvents.filter(event => {
    const eventDate = getEventDate(event);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  const displayTodayEvents = todayEvents.length > 0 ? todayEvents : selectedDateEvents.filter(event => {
    const today = new Date();
    const eventDate = getEventDate(event);
    return eventDate.toDateString() === today.toDateString();
  });

  const thisWeekEvents = allEvents.filter(event => {
    const eventDate = getEventDate(event);
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return eventDate >= weekStart && eventDate <= weekEnd;
  });

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
    trackEvent(LEARNER_SCHEDULE_EVENTS.CALENDAR_NAVIGATE_MONTH, {
      direction,
      month: newMonth.toLocaleDateString([], { month: 'long', year: 'numeric' }),
    });
  };

  /**
   * Get icon for SDK calendar event based on category/status
   */
  const getSDKEventIcon = (event: SDKCalendarEventDto) => {
    // SDK events are course deadlines, so use appropriate icon
    if (event.assignment_type === 'exam' || event.assignment_type === 'assessment') {
      return <CheckCircle className="w-4 h-4" />;
    }
    if (event.assignment_type === 'certification') {
      return <FileText className="w-4 h-4" />;
    }
    return <BookOpen className="w-4 h-4" />; // Default to course/assignment icon
  };

  /**
   * Get color for SDK calendar event based on status
   */
  const getSDKEventColor = (event: SDKCalendarEventDto) => {
    // Color based on status
    switch (event.status) {
      case 'overdue':
        return 'bg-red-600';
      case 'today':
        return 'bg-orange-500';
      case 'upcoming':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleEventClick = (event: SDKCalendarEventDto) => {
    if (!canWrite) return;

    trackEvent(LEARNER_SCHEDULE_EVENTS.EVENT_CLICK, {
      event_id: event.event_id,
      event_title: getEventTitle(event),
      event_status: event.status,
      course_id: event.course_id,
    });

    if (event.course_id) {
      // Navigate to exam start page for certifications, otherwise go to study page
      if (event.assignment_type === 'certification') {
        navigate(`/learn/exams/start/${event.course_id}`);
      } else {
        navigate(`/learn/study/${event.course_id}`);
      }
    } else {
      setSelectedEvent(event);
      setShowEventModal(true);
      trackEvent(LEARNER_SCHEDULE_EVENTS.EVENT_MODAL_OPEN, {
        event_id: event.event_id,
        event_title: getEventTitle(event),
      });
    }
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setViewMode('day');
    trackEvent(LEARNER_SCHEDULE_EVENTS.CALENDAR_DAY_CLICK, {
      date: date.toISOString(),
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get unique courses for events on same day (to show in tooltip)
   */
  const getUniqueCourses = (events: SDKCalendarEventDto[]): string => {
    const courses = [...new Set(events.map(e => e.course_title).filter(Boolean))];
    return courses.join(', ');
  };

  /**
   * Format event name for calendar display (Microsoft Teams style)
   */
  const getFormattedEventName = (event: SDKCalendarEventDto): { label: string; subtitle?: string } => {
    if (event.course_title) {
      return {
        label: event.course_title,
      };
    }
    return { label: event.title };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <SkeletonLoader type="title" className="mb-2 bg-white/20" />
              <SkeletonLoader type="text" className="w-2/3 bg-white/20" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <SkeletonLoader type="text" className="h-3 w-1/2 mb-2 bg-white/20" />
                <SkeletonLoader type="title" className="h-8 w-2/3 bg-white/20" />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="lg:col-span-3 bg-white rounded-lg border border-gray-200 p-4">
            <SkeletonLoader type="title" className="mb-4" />
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonLoader key={i} type="text" className="h-24" />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <CardSkeleton key={i} lines={3} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2 flex items-center gap-2">
              <Calendar className="w-6 h-6 sm:w-7 sm:h-7" />
              Learning Schedule
            </h1>
            <p className="text-indigo-100 text-sm sm:text-base">
              Manage your learning calendar, assignments, and deadlines
            </p>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {statsLoading ? (
            [1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <SkeletonLoader type="text" className="h-3 w-1/2 mb-2 bg-white/20" />
                <SkeletonLoader type="title" className="h-8 w-2/3 bg-white/20" />
              </div>
            ))
          ) : (
            <>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors">
                <p className="text-indigo-100 text-xs font-medium">Today's Events</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1">{calendarStats?.today_count || 0}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors">
                <p className="text-indigo-100 text-xs font-medium">This Week</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1">{thisWeekEvents.length}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors">
                <p className="text-indigo-100 text-xs font-medium">Overdue</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1">{calendarStats?.overdue_count || 0}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors">
                <p className="text-indigo-100 text-xs font-medium">Upcoming</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1">{calendarStats?.upcoming_count || 0}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Calendar */}
        <div className="lg:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Single Line Header with All Controls */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
              {/* Left: Month/Week Navigation (only for month view) */}
              {viewMode === 'month' && (
                <div className="flex items-center justify-between sm:justify-start gap-1 sm:gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Previous month"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-fit text-center">
                    {currentMonth.toLocaleDateString([], { month: 'long', year: 'numeric' })}
                  </h2>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Next month"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              )}

              {/* Week/Day Headers */}
              {viewMode === 'week' && (
                <div className="flex items-center justify-between sm:justify-start gap-1 sm:gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() - 7);
                      setSelectedDate(newDate);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Previous week"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-fit text-center">
                    Week {getWeekOfMonth(selectedDate)} - {selectedDate.toLocaleDateString([], { month: 'long', year: 'numeric' })}
                  </h2>
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() + 7);
                      setSelectedDate(newDate);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Next week"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              )}

              {viewMode === 'day' && (
                <div className="flex items-center justify-between sm:justify-start gap-1 sm:gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() - 1);
                      setSelectedDate(newDate);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Previous day"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-fit text-center">
                    {selectedDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h2>
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() + 1);
                      setSelectedDate(newDate);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Next day"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              )}

              {/* Spacer (desktop only — on mobile the controls wrap to their own row) */}
              <div className="hidden sm:block flex-1" />

              {/* View Mode Selector + Today Button */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setCurrentMonth(new Date());
                    setSelectedDate(new Date());
                    trackEvent(LEARNER_SCHEDULE_EVENTS.CALENDAR_TODAY_CLICK);
                  }}
                  className="px-3 py-2 text-sm text-gray-700 border border-gray-300 sm:border-transparent hover:bg-gray-100 rounded-lg transition-colors font-medium"
                >
                  Today
                </button>
                <div className="flex flex-1 sm:flex-none border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                  {['month', 'week', 'day'].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => {
                        setViewMode(mode as 'month' | 'week' | 'day');
                        trackEvent(LEARNER_SCHEDULE_EVENTS.CALENDAR_VIEW_CHANGE, {
                          view_mode: mode,
                        });
                      }}
                      className={`flex-1 sm:flex-none px-3 py-2 text-xs font-medium capitalize transition-all ${
                        viewMode === mode
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          <div className="p-2 sm:p-4">
            {/* Color Legend */}
            <div className="mb-3 sm:mb-4 pb-3 px-1 sm:px-0 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                <span className="font-semibold text-gray-700">Status:</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-600"></div>
                  <span className="text-gray-600">Overdue</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-gray-600">Due Today</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600">Upcoming</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">Completed</span>
                </div>
              </div>
            </div>

            {viewMode === 'month' && (
              <>
                {/* No separate navigation needed - it's in header now */}

                {/* Days of week header */}
                <div className="grid grid-cols-7 gap-1 mb-1 sm:mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="py-1.5 sm:p-3 text-center text-xs sm:text-sm font-semibold text-gray-700">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, index) => {
                    // Group events by status for compact display
                    const overdueCount = day.events.filter(e => e.status === 'overdue').length;
                    const todayCount = day.events.filter(e => e.status === 'today').length;
                    const upcomingCount = day.events.filter(e => e.status === 'upcoming').length;
                    const completedCount = day.events.filter(e => e.status === 'completed').length;

                    return (
                      <div
                        key={index}
                        className={`group min-h-[52px] sm:min-h-[100px] p-1 sm:p-2 border rounded-md sm:rounded-lg cursor-pointer transition-all ${
                          !day.isCurrentMonth
                            ? 'text-gray-400 bg-gray-50 border-gray-100'
                            : 'border-gray-200 hover:border-indigo-300 hover:shadow-md bg-white'
                        } ${day.isToday ? 'bg-indigo-50 border-indigo-300 border-2' : ''}`}
                        onClick={() => handleDayClick(day.date)}
                        title={day.events.length > 0 ? getUniqueCourses(day.events) : 'No events'}
                      >
                        {/* Date Number */}
                        <div className="flex items-center justify-center sm:justify-between sm:mb-2">
                          <div className={`text-sm font-bold ${
                            day.isToday ? 'text-indigo-600' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                            {day.date.getDate()}
                          </div>
                          {day.events.length > 0 && (
                            <div className="hidden sm:block text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-1.5 py-0.5">
                              {day.events.length}
                            </div>
                          )}
                        </div>

                        {/* Mobile: a cell is ~45px wide — too narrow for titles, so
                            show one status-coloured dot per event (max 3, then +N).
                            Tapping the day opens the Day view with the full list. */}
                        {day.events.length > 0 && (
                          <div className="sm:hidden flex items-center justify-center gap-0.5 mt-1.5">
                            {day.events.slice(0, 3).map((event) => (
                              <span key={event.event_id} className={`w-1.5 h-1.5 rounded-full ${getSDKEventColor(event)}`} />
                            ))}
                            {day.events.length > 3 && (
                              <span className="text-[9px] leading-none font-semibold text-gray-500">+{day.events.length - 3}</span>
                            )}
                          </div>
                        )}

                        {/* Events Container - Compact Dots & Badges (sm and up) */}
                        <div className="hidden sm:block space-y-1 flex-1">
                          {day.events.length > 0 ? (
                            <div className="space-y-1.5">
                              {/* Show first 3 events as compact badges */}
                              {day.events.slice(0, 3).map((event) => {
                                const formatted = getFormattedEventName(event);
                                return (
                                  <div
                                    key={event.event_id}
                                    className={`flex items-start gap-1.5 group/event rounded px-1 py-0.5 transition-colors ${canWrite ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEventClick(event);
                                    }}
                                    title={canWrite ? formatted.label : 'You do not have permission to access courses'}
                                  >
                                    {/* Status Dot */}
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${getSDKEventColor(event)}`} />
                                    {/* Course Name - Compact */}
                                    <div className="text-xs text-gray-700 truncate flex-1 font-medium group-hover/event:text-indigo-600">
                                      {formatted.label.length > 20 ? formatted.label.substring(0, 20) + '...' : formatted.label}
                                    </div>
                                  </div>
                                );
                              })}

                              {/* More indicator - Click to see day view */}
                              {day.events.length > 3 && (
                                <div className="text-xs text-indigo-600 font-semibold px-1 py-1 hover:bg-indigo-50 rounded transition-colors flex items-center gap-1">
                                  <span className="text-gray-400">+</span>
                                  {day.events.length - 3} more
                                </div>
                              )}

                              {/* Status Summary Badges - Only show if 4+ events */}
                              {day.events.length >= 4 && (
                                <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100">
                                  {overdueCount > 0 && (
                                    <div className="text-xs bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 font-medium">
                                      {overdueCount} ⚠️
                                    </div>
                                  )}
                                  {todayCount > 0 && (
                                    <div className="text-xs bg-orange-100 text-orange-700 rounded-full px-1.5 py-0.5 font-medium">
                                      {todayCount} 📅
                                    </div>
                                  )}
                                  {upcomingCount > 0 && (
                                    <div className="text-xs bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 font-medium">
                                      {upcomingCount} 🔜
                                    </div>
                                  )}
                                  {completedCount > 0 && (
                                    <div className="text-xs bg-green-100 text-green-700 rounded-full px-1.5 py-0.5 font-medium">
                                      {completedCount} ✓
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-300 text-center mt-4">No events</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {viewMode === 'week' && (
              <div className="space-y-4">
                {(() => {
                  const startOfWeek = new Date(selectedDate);
                  startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());

                  const weekDays = Array.from({ length: 7 }, (_, i) => {
                    const date = new Date(startOfWeek);
                    date.setDate(startOfWeek.getDate() + i);
                    return date;
                  });

                  return (
                    /* Mobile: one row per day (date chip | events) — 7 columns of
                       ~40px could only show 2-3 letters of each title. */
                    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 sm:gap-3">
                        {weekDays.map((day, index) => {
                          const dayEvents = allEvents.filter(event => {
                            const eventDate = getEventDate(event);
                            return eventDate.toDateString() === day.toDateString();
                          });

                          const isToday = day.toDateString() === new Date().toDateString();

                          return (
                            <div key={index} className="flex sm:block items-start gap-3 sm:space-y-2 pb-2 sm:pb-0 border-b border-gray-100 last:border-0 sm:border-0">
                              <div
                                className={`w-14 sm:w-auto flex-shrink-0 text-center p-2 sm:p-3 rounded-lg font-medium transition-all cursor-pointer ${
                                  isToday
                                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300 hover:bg-indigo-200'
                                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                                }`}
                                onClick={() => handleDayClick(day)}
                              >
                                <div className="text-xs font-semibold uppercase">
                                  {day.toLocaleDateString([], { weekday: 'short' })}
                                </div>
                                <div className="text-lg font-bold">
                                  {day.getDate()}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0 space-y-1 sm:space-y-1.5 sm:min-h-[220px]">
                                {dayEvents.length > 0 ? (
                                  <>
                                    {/* Show first 5 events as compact dots */}
                                    {dayEvents.slice(0, 5).map(event => {
                                      const formatted = getFormattedEventName(event);
                                      return (
                                        <div
                                          key={event.event_id}
                                          className={`flex items-center gap-1.5 p-1.5 rounded transition-colors group/event ${canWrite ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60'}`}
                                          onClick={() => handleEventClick(event)}
                                          title={canWrite ? formatted.label : 'You do not have permission to access courses'}
                                        >
                                          {/* Status Dot */}
                                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getSDKEventColor(event)}`} />
                                          {/* Course Name */}
                                          <div className="text-xs text-gray-700 truncate flex-1 font-medium group-hover/event:text-indigo-600">
                                            {/* Full row width on mobile — let CSS truncate instead of 15 chars */}
                                            <span className="sm:hidden">{formatted.label}</span>
                                            <span className="hidden sm:inline">
                                              {formatted.label.length > 15 ? formatted.label.substring(0, 15) + '...' : formatted.label}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}

                                    {/* More indicator for week view */}
                                    {dayEvents.length > 5 && (
                                      <div className="text-xs text-indigo-600 font-semibold px-1.5 py-1 hover:bg-indigo-50 rounded transition-colors text-center">
                                        +{dayEvents.length - 5} more
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-center justify-start sm:justify-center h-full py-3 sm:py-0 text-gray-300">
                                    <p className="text-xs">No events</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                })()}
              </div>
            )}

            {viewMode === 'day' && (
              <div className="space-y-4">
                {/* No separate header needed - navigation is in top header */}
                <div className="space-y-4">
                  {selectedDateEvents.length > 0 ? (
                    selectedDateEvents
                      .sort((a, b) => getEventDate(a).getTime() - getEventDate(b).getTime())
                      .map(event => (
                        <div
                          key={event.event_id}
                          className={`border border-gray-200 rounded-lg p-3 sm:p-4 transition-all bg-white ${canWrite ? 'hover:shadow-lg cursor-pointer hover:border-indigo-300' : 'cursor-not-allowed opacity-60'}`}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`p-2 sm:p-3 ${getSDKEventColor(event)} rounded-lg text-white flex-shrink-0`}>
                                {getSDKEventIcon(event)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">{getEventTitle(event)}</h3>
                                  {event.course_id && (
                                    <ExternalLink className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                  )}
                                </div>
                                {event.course_title && (
                                  <p className="text-sm text-gray-600 mt-1 font-medium">📚 {event.course_title}</p>
                                )}
                                {event.description && (
                                  <p className="text-sm text-gray-600 mt-2">{event.description}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-4 mt-2 sm:mt-3 text-xs sm:text-sm text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <CalendarIcon className="w-4 h-4 text-indigo-500" />
                                    {getEventDate(event).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-indigo-500" />
                                    {event.days_until_deadline === 0 ? 'Due today' : event.days_until_deadline > 0 ? `${event.days_until_deadline} days left` : `${Math.abs(event.days_until_deadline)} days overdue`}
                                  </span>
                                  {event.is_mandatory && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Mandatory
                                    </span>
                                  )}
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {event.progress_percentage}% Complete
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="font-medium">No events scheduled for this day</p>
                      <p className="text-sm mt-1">Select a different date to view other events</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Today's Events */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                Today's Schedule
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {statsLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 p-2">
                    <SkeletonLoader type="custom" className="w-8 h-8 rounded" />
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader type="text" className="h-4 w-3/4" />
                      <SkeletonLoader type="text" className="h-3 w-1/2" />
                    </div>
                  </div>
                ))
              ) : displayTodayEvents.length > 0 ? (
                displayTodayEvents.map(event => (
                  <div key={event.event_id} className={`flex items-start gap-3 p-2 rounded-lg transition-colors group ${canWrite ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                    <div className={`p-1.5 ${getSDKEventColor(event)} rounded text-white flex-shrink-0 group-hover:shadow-md transition-all`}>
                      {getSDKEventIcon(event)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium text-gray-900 truncate ${canWrite ? 'hover:text-indigo-600 cursor-pointer' : 'cursor-not-allowed'}`}
                        onClick={() => handleEventClick(event)}
                      >
                        {getEventTitle(event)}
                      </p>
                      {event.course_title && event.title && event.title !== event.course_title && (
                        <p className="text-xs text-gray-500 truncate">{event.course_title}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        {formatTime(event.deadline_date)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No events scheduled
                </p>
              )}
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                Upcoming Deadlines
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {statsLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 p-2">
                    <SkeletonLoader type="custom" className="w-4 h-4 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader type="text" className="h-4 w-3/4" />
                      <SkeletonLoader type="text" className="h-3 w-1/2" />
                    </div>
                  </div>
                ))
              ) : upcomingEvents.length > 0 ? (
                upcomingEvents.slice(0, 3).map(event => {
                  return (
                    <div key={event.event_id} className={`flex items-start gap-3 p-2 bg-orange-50 rounded-lg transition-colors group ${canWrite ? 'hover:bg-orange-100 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                      <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium text-gray-900 truncate ${canWrite ? 'hover:text-indigo-600 cursor-pointer' : 'cursor-not-allowed'}`}
                          onClick={() => handleEventClick(event)}
                        >
                          {getEventTitle(event)}
                        </p>
                        {event.course_title && event.title && event.title !== event.course_title && (
                          <p className="text-xs text-gray-600 truncate">{event.course_title}</p>
                        )}
                        <p className="text-xs text-orange-600 font-medium">
                          Due in {event.days_until_deadline} day{event.days_until_deadline !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No upcoming deadlines
                </p>
              )}
            </div>
          </div>

          {/* Overdue Assignments */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Overdue Assignments
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {statsLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 p-2">
                    <SkeletonLoader type="custom" className="w-4 h-4 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader type="text" className="h-4 w-3/4" />
                      <SkeletonLoader type="text" className="h-3 w-1/2" />
                    </div>
                  </div>
                ))
              ) : overdueEvents.length > 0 ? (
                overdueEvents.slice(0, 3).map(event => {
                  return (
                    <div key={event.event_id} className={`flex items-start gap-3 p-2 bg-red-50 rounded-lg transition-colors group ${canWrite ? 'hover:bg-red-100 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium text-gray-900 truncate ${canWrite ? 'hover:text-indigo-600 cursor-pointer' : 'cursor-not-allowed'}`}
                          onClick={() => handleEventClick(event)}
                        >
                          {getEventTitle(event)}
                        </p>
                        {event.course_title && event.title && event.title !== event.course_title && (
                          <p className="text-xs text-gray-600 truncate">{event.course_title}</p>
                        )}
                        <p className="text-xs text-red-600 font-medium">
                          Overdue by {Math.abs(event.days_until_deadline)} day{Math.abs(event.days_until_deadline) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No overdue assignments
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 ${getSDKEventColor(selectedEvent)} rounded-lg text-white`}>
                    {getSDKEventIcon(selectedEvent)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{getEventTitle(selectedEvent)}</h2>
                    <p className="text-sm text-gray-600 capitalize">{selectedEvent.category || 'Course Deadline'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                    trackEvent(LEARNER_SCHEDULE_EVENTS.EVENT_MODAL_CLOSE);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {selectedEvent.description && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700">{selectedEvent.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Deadline</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                    <span>{formatDate(selectedEvent.deadline_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700 mt-2">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {selectedEvent.days_until_deadline === 0 ? 'Due today' : selectedEvent.days_until_deadline > 0 ? `${selectedEvent.days_until_deadline} days remaining` : `Overdue by ${Math.abs(selectedEvent.days_until_deadline)} days`}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Progress</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{selectedEvent.progress_percentage}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${selectedEvent.progress_percentage}%`
                      }}
                    />
                  </div>
                </div>
              </div>

              {selectedEvent.course_title && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Course</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                    <span>{selectedEvent.course_title}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {selectedEvent.is_mandatory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    Mandatory
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  selectedEvent.status === 'overdue' ? 'bg-red-100 text-red-800' :
                  selectedEvent.status === 'today' ? 'bg-orange-100 text-orange-800' :
                  selectedEvent.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {selectedEvent.status}
                </span>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="flex gap-3">
                {selectedEvent.course_id && (
                  <button
                    onClick={() => {
                      if (!canWrite) return;
                      trackEvent(LEARNER_SCHEDULE_EVENTS.EVENT_GO_TO_COURSE, {
                        course_id: selectedEvent.course_id,
                        course_title: selectedEvent.course_title,
                      });
                      navigate(`/learn/study/${selectedEvent.course_id}`);
                    }}
                    disabled={!canWrite}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${canWrite ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    title={!canWrite ? 'You do not have permission to access courses' : undefined}
                  >
                    Go to Course
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                    trackEvent(LEARNER_SCHEDULE_EVENTS.EVENT_MODAL_CLOSE);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
